import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { HotTable } from '@handsontable/react';
import 'handsontable/dist/handsontable.full.min.css';
import SubmitButton from '../../../CommonComponents/SubmitButton';
import { Box, Flex, useToast } from '@chakra-ui/react';
import ImageModal from '../../../FunctionalComponents/Modal/ImageModal';
import { DataNotFound } from '../../../CommonComponents/DataNotFound';
import { deleteManMadeHeritageData, getLocationData, updateManMadeHeritageData } from '../../../services/Location';
import { getImageURL } from '../../../util/getImage';
import { getRemovedValues, showImages } from '../../../util/helper';
import FormLoader from '../../../CommonComponents/FormLoader';
import { showSuccessToast } from '../../../util/errorHandler';



const ManMadeHeritage = ({ onCountLoad }) => {

  const { alias } = useParams();
  const [landingPageData, setLandingPageData] = useState();
  const [columnHeaders, setColumnHeaders] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [quickSave, setQuickSave] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState("");

  const hotRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    const getLandingPageData = async () => {
      try {
        if (alias) {
          const result = await getLocationData(alias, 'bodyofwork', 'man-made-heritage');

          if (result?.data?.length > 0) {
            setLandingPageData(result);

            const updatedData = result.data?.map((item) => ({
              ...item, // Spread existing keys and values
              image: getImageURL(item.image_alias), // Generate or add a new value for "image"
            }));

            const keys = Object.keys(updatedData[0]);
            setColumnHeaders(keys);

            const rowData = updatedData?.map((item) =>
              keys.map((key) => item[key] ?? "") // Extract values by key
            );
            setTableData(rowData);
            onCountLoad?.(result.data.length);
          } else {
            onCountLoad?.(0);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getLandingPageData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alias, quickSave]);

  // const handleAutoSave = async (changes, source) => {
  //   if (source === "edit") {
  //     // Iterate over the changes array
  //     for (const [row, col, oldValue, newValue] of changes) {
  //       // Retrieve the column header using the column index (col)
  //       const editedField = columnHeaders[col]; // Assuming columnHeaders is an array of header names

  //       // If the value has changed (old value is different from new value)
  //       if (oldValue !== newValue) {
  //         const updateValue = newValue;

  //         // Prepare the data to be sent in the API request
  //         const data = {
  //           "_id": tableData?.[row]?.[0], // Assuming first column (_id) is at index 0
  //           [editedField]: updateValue,    // Dynamically set the field name (e.g., "landing_page_title")
  //         };

  //         // Make the API call to update the data
  //         try {
  //           await updateManMadeHeritageData(alias, data);

  //         } catch (error) {
  //           console.error("Error saving data:", error);
  //         }
  //       }
  //     }
  //   }
  // };

  const saveClickCallback = async () => {
    const hot = hotRef.current.hotInstance;
    const data = hot.getData();
    const headers = hot.getColHeader();

    const formattedData = data
      ?.filter(row => row.some(cell => cell !== null && cell !== ''))
      ?.map(row => {
        const rowData = {};
        headers?.forEach((header, index) => {
          if (row[index] !== null && row[index] !== '') {
            rowData[header] = row[index];
          }
        });

        return rowData;
      });

    const deletedItems = getRemovedValues(landingPageData?.data, formattedData);

    if (formattedData?.length > 0) {
      setIsSubmitting(true);
      try {
        // Use the same function for both new and existing records
        const result = await updateManMadeHeritageData(alias, formattedData);

        if (result?.status === 200) {
          setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data saved successfully");
        }

      } catch (error) {
        console.error("Error saving row:", error);
      }
      finally {
        setIsSubmitting(false);
      }
    }

    if (deletedItems?.length > 0) {
      try {
        // Use the same function for both new and existing records
        const result = await deleteManMadeHeritageData(alias, deletedItems);

        if (result?.status === 200) {
                    showSuccessToast(toast, "Data saved successfully");
        }
      } catch (error) {
        console.error("Error saving row:", error);
      }
    }
  };

  const openImageModal = (src) => {
    setModalImageSrc(src);
    setIsModalOpen(true);
  };

  const closeImageModal = () => {
    setIsModalOpen(false);
    setModalImageSrc("");
  };

  const columns = showImages(columnHeaders, openImageModal)

  const hotSettings = {
    data: tableData,
    colHeaders: columnHeaders,
    rowHeaders: true,
    height: "auto",
    // afterChange: handleAutoSave,
    licenseKey: "non-commercial-and-evaluation",
    contextMenu: true,
    cells: (row, col) => {
      if (col === 0) {
        return { readOnly: true }; // Make the first column read-only
      }
      return {};
    },
    beforeCopy: (data, coords) => {
      const hot = hotRef.current?.hotInstance; // Get the Handsontable instance from the ref
      if (!hot) return;

      coords.forEach(coord => {
        for (let row = coord.startRow; row <= coord.endRow; row++) {
          for (let col = coord.startCol; col <= coord.endCol; col++) {
            // Skip hidden columns
            if (hot?.getPlugin('hiddenColumns')?.isHidden(col)) {
              data[row - coord.startRow][col - coord.startCol] = ''; // Clear hidden column data
            }
          }
        }
      });
    },
    hiddenColumns: {
      columns: [0, 6, 7], // Hides the first column (index 0)
      indicators: false, // No visual indicator for hidden columns
    },
    plugins: ["hiddenColumns"],
  };

  return (
    <Box>
      {isLoading ? (
        <FormLoader />
      ) : (
          <>
      {landingPageData?.data?.length > 0 ? (
        <>
                <Flex justifyContent="flex-start" mb={4}>
            <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback} />
          </Flex>
          {/* Handsontable */}
          <HotTable
            ref={hotRef}
            {...hotSettings}
            columns={columns}
          />
          <ImageModal isModalOpen={isModalOpen} closeImageModal={closeImageModal} modalImageSrc={modalImageSrc} />
                <Flex justifyContent="flex-start" mt={4}>
            <SubmitButton isLoading={isSubmitting} onClick={saveClickCallback} />
          </Flex>
        </>
      ) :
        (
          <DataNotFound />
        )}
    </>
      )}
    </Box>

  );
};

export default ManMadeHeritage;
